import { jsPDF } from 'jspdf';

const COLORS = {
    sage: [92, 122, 107],
    warm: [196, 149, 106],
    slate: [45, 58, 53],
    slateLight: [140, 158, 152],
    cream: [248, 245, 240],
    white: [255, 255, 255],
    border: [216, 224, 220],
};

const addPageIfNeeded = (doc, y, margin, pageHeight, lineHeight = 20) => {
    if (y + lineHeight > pageHeight - margin) {
        doc.addPage();
        return margin;
    }
    return y;
};

const drawHeader = (doc, practiceName, title, subtitle, pageWidth, margin) => {
    // Header background
    doc.setFillColor(...COLORS.slate);
    doc.rect(0, 0, pageWidth, 42, 'F');

    // Practice name
    doc.setTextColor(...COLORS.warm);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(11);
    doc.text(practiceName || 'Transformation Companion', margin, 16);

    // Title
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(title, margin, 28);

    // Subtitle
    if (subtitle) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...COLORS.slateLight);
        doc.text(subtitle, margin, 37);
    }

    return 58;
};

const drawFooter = (doc, practiceName, pageNum, totalPages, isConfidential, pageWidth, margin) => {
    const y = doc.internal.pageSize.height - 16;
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.line(margin, y - 6, pageWidth - margin, y - 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slateLight);
    if (isConfidential) {
        doc.text('CONFIDENTIAL — FOR PROFESSIONAL USE ONLY', margin, y);
    } else {
        doc.text(practiceName || 'Transformation Companion', margin, y);
    }
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin, y, { align: 'right' });
    doc.text(new Date().toLocaleDateString(), pageWidth / 2, y, { align: 'center' });
};

const drawSectionTitle = (doc, text, y, margin, pageWidth) => {
    doc.setFillColor(...COLORS.cream);
    doc.rect(margin, y - 5, pageWidth - margin * 2, 14, 'F');
    doc.setDrawColor(...COLORS.sage);
    doc.setLineWidth(2);
    doc.line(margin, y - 5, margin, y + 9);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.sage);
    doc.text(text.toUpperCase(), margin + 8, y + 4);
    return y + 20;
};

const drawBodyText = (doc, text, y, margin, pageWidth, pageHeight, fontSize = 10) => {
    if (!text) return y;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize);
    doc.setTextColor(...COLORS.slate);
    const lines = doc.splitTextToSize(text, pageWidth - margin * 2);
    lines.forEach(line => {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 14);
        doc.text(line, margin, y);
        y += 14;
    });
    return y + 4;
};

const drawBullet = (doc, text, y, margin, pageWidth, pageHeight) => {
    if (!text) return y;
    y = addPageIfNeeded(doc, y, margin, pageHeight, 14);
    doc.setFillColor(...COLORS.sage);
    doc.circle(margin + 3, y - 3, 1.5, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.slate);
    const lines = doc.splitTextToSize(text, pageWidth - margin * 2 - 12);
    lines.forEach((line, i) => {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 14);
        doc.text(line, margin + 10, y);
        y += 14;
    });
    return y + 2;
};

const drawQuoteBlock = (doc, quote, y, margin, pageWidth, pageHeight) => {
    y = addPageIfNeeded(doc, y, margin, pageHeight, 30);
    doc.setFillColor(250, 243, 224);
    const lines = doc.splitTextToSize(`"${quote}"`, pageWidth - margin * 2 - 20);
    const blockHeight = lines.length * 13 + 16;
    doc.rect(margin, y - 6, pageWidth - margin * 2, blockHeight, 'F');
    doc.setFillColor(...COLORS.warm);
    doc.rect(margin, y - 6, 3, blockHeight, 'F');
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.slate);
    lines.forEach(line => {
        doc.text(line, margin + 10, y + 4);
        y += 13;
    });
    return y + 16;
};

// ══════════════════════════════════════════
//  COACH SESSION SUMMARY PDF
// ══════════════════════════════════════════
export const exportCoachSessionPDF = (session, client, practiceName) => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 48;
    const s = session.summary || {};
    const goals = session.goals || [];
    const breakthroughs = session.breakthrough_quotes || [];

    let y = drawHeader(
        doc, practiceName,
        session.title || 'Session Summary',
        `${client.name} · ${new Date(session.date + 'T12:00:00').toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`,
        pageWidth, margin
    );

    // Overview
    if (s.overview) {
        y = drawSectionTitle(doc, 'Session Overview', y, margin, pageWidth);
        y = drawBodyText(doc, s.overview, y, margin, pageWidth, pageHeight);
        y += 8;
    }

    // Key themes
    if (s.keyThemes?.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Key Themes', y, margin, pageWidth);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.slate);
        const themeText = s.keyThemes.join('  ·  ');
        doc.text(themeText, margin, y);
        y += 20;
    }

    // Breakthroughs
    if (s.breakthroughs) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Breakthroughs & Insights', y, margin, pageWidth);
        y = drawBodyText(doc, s.breakthroughs, y, margin, pageWidth, pageHeight);
        y += 8;
    }

    // Challenges
    if (s.challenges) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Challenges & Complexities', y, margin, pageWidth);
        y = drawBodyText(doc, s.challenges, y, margin, pageWidth, pageHeight);
        y += 8;
    }

    // Client growth
    if (s.clientGrowth) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Client Growth', y, margin, pageWidth);
        y = drawBodyText(doc, s.clientGrowth, y, margin, pageWidth, pageHeight);
        y += 8;
    }

    // Coaching observations
    if (s.clinicalObservations) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Coaching Observations', y, margin, pageWidth);
        y = drawBodyText(doc, s.clinicalObservations, y, margin, pageWidth, pageHeight);
        y += 8;
    }

    // Goals
    if (goals.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Session Goals', y, margin, pageWidth);
        goals.forEach(g => {
            const icon = g.status === 'met' ? '[MET]' : g.status === 'partial' ? '[PARTIAL]' : g.status === 'unmet' ? '[NOT MET]' : '[PENDING]';
            y = drawBullet(doc, `${g.text}  ${icon}`, y, margin, pageWidth, pageHeight);
            if (g.assessment) {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(9);
                doc.setTextColor(...COLORS.slateLight);
                const aLines = doc.splitTextToSize(g.assessment, pageWidth - margin * 2 - 12);
                aLines.forEach(line => {
                    y = addPageIfNeeded(doc, y, margin, pageHeight, 12);
                    doc.text(line, margin + 10, y);
                    y += 12;
                });
                y += 4;
            }
        });
        y += 8;
    }

    // Action items
    if (s.actionItems?.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Action Items', y, margin, pageWidth);
        s.actionItems.forEach(a => { y = drawBullet(doc, a, y, margin, pageWidth, pageHeight); });
        y += 8;
    }

    // Breakthrough quotes
    if (breakthroughs.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Breakthrough Moments', y, margin, pageWidth);
        breakthroughs.forEach(bq => {
            if (bq.quote) y = drawQuoteBlock(doc, bq.quote, y, margin, pageWidth, pageHeight);
            if (bq.significance) {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(9);
                doc.setTextColor(...COLORS.slateLight);
                y = addPageIfNeeded(doc, y, margin, pageHeight, 14);
                doc.text(bq.significance, margin + 10, y);
                y += 16;
            }
        });
        y += 8;
    }

    // Recommendations
    if (s.coachRecommendations) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Recommendations for Next Session', y, margin, pageWidth);
        y = drawBodyText(doc, s.coachRecommendations, y, margin, pageWidth, pageHeight);
        y += 8;
    }

    // Opening prompts
    if (s.nextSessionPrompts?.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Opening Prompts for Next Session', y, margin, pageWidth);
        s.nextSessionPrompts.forEach(p => { y = drawBullet(doc, p, y, margin, pageWidth, pageHeight); });
        y += 8;
    }

    // Coach reflection
    if (session.post_reflection) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Coach Reflection', y, margin, pageWidth);
        y = drawBodyText(doc, session.post_reflection, y, margin, pageWidth, pageHeight);
    }

    // Footers
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        drawFooter(doc, practiceName, i, totalPages, true, pageWidth, margin);
    }

    doc.save(`coach_summary_${client.name.replace(/\s+/g, '_')}_${session.date}.pdf`);
};

// ══════════════════════════════════════════
//  CLIENT SESSION SUMMARY PDF
// ══════════════════════════════════════════
export const exportClientSessionPDF = (session, client, practiceName, options, coachMessage) => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 48;
    const s = session.summary || {};
    const goals = session.goals || [];
    const breakthroughs = (session.breakthrough_quotes || []).filter(b => b.starred);

    let y = drawHeader(
        doc, practiceName,
        'Your Session Summary',
        `${client.name} · ${new Date(session.date + 'T12:00:00').toLocaleDateString('default', { month: 'long', day: 'numeric', year: 'numeric' })}`,
        pageWidth, margin
    );

    // Warm intro
    y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.slateLight);
    doc.text(`A reflection on your coaching session`, margin, y);
    y += 24;

    // Overview
    if (options.overview && s.overview) {
        y = drawSectionTitle(doc, 'Session Overview', y, margin, pageWidth);
        y = drawBodyText(doc, s.overview, y, margin, pageWidth, pageHeight);
        y += 8;
    }

    // Key themes
    if (options.themes && s.keyThemes?.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'What We Explored', y, margin, pageWidth);
        s.keyThemes.forEach(t => { y = drawBullet(doc, t, y, margin, pageWidth, pageHeight); });
        y += 8;
    }

    // Starred breakthroughs
    if (options.breakthroughs && breakthroughs.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Breakthrough Moments', y, margin, pageWidth);
        breakthroughs.forEach(bq => {
            if (bq.quote) y = drawQuoteBlock(doc, bq.quote, y, margin, pageWidth, pageHeight);
        });
        y += 8;
    }

    // Action items
    if (options.commitments && s.actionItems?.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Your Commitments', y, margin, pageWidth);
        s.actionItems.forEach(a => { y = drawBullet(doc, a, y, margin, pageWidth, pageHeight); });
        y += 8;
    }

    // Goals
    if (options.goals && goals.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Session Goals', y, margin, pageWidth);
        goals.forEach(g => {
            const icon = g.status === 'met' ? '[MET]' : g.status === 'partial' ? '[PARTIAL]' : '';
            y = drawBullet(doc, `${g.text}${icon ? '  ' + icon : ''}`, y, margin, pageWidth, pageHeight);
        });
        y += 8;
    }

    // Steps forward
    if (options.stepsForward && s.coachRecommendations) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Steps Forward', y, margin, pageWidth);
        y = drawBodyText(doc, s.coachRecommendations, y, margin, pageWidth, pageHeight);
        y += 8;
    }

    // Custom coach message
    if (options.coachMessage && coachMessage?.trim()) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 60);
        y = drawSectionTitle(doc, 'A Note From Your Coach', y, margin, pageWidth);
        doc.setFillColor(235, 240, 237);
        const msgLines = doc.splitTextToSize(coachMessage, pageWidth - margin * 2 - 20);
        const msgHeight = msgLines.length * 14 + 20;
        doc.rect(margin, y - 6, pageWidth - margin * 2, msgHeight, 'F');
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.slate);
        msgLines.forEach(line => {
            y = addPageIfNeeded(doc, y, margin, pageHeight, 14);
            doc.text(line, margin + 10, y + 4);
            y += 14;
        });
        y += 20;
    }

    // Footers
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        drawFooter(doc, practiceName, i, totalPages, false, pageWidth, margin);
    }

    doc.save(`session_summary_${client.name.replace(/\s+/g, '_')}_${session.date}.pdf`);
};

// ══════════════════════════════════════════
//  CLIENT PROGRESS REPORT PDF
// ══════════════════════════════════════════
export const exportProgressReportPDF = (client, sessions, practiceName, transformationArc, options, coachMessage) => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 48;

    const firstSession = sessions[sessions.length - 1];
    const lastSession = sessions[0];
    const achievedGoals = (client.long_goals || []).filter(g => g.status === 'achieved');
    const activeGoals = (client.long_goals || []).filter(g => g.status === 'active');
    const allStarred = sessions.flatMap(s =>
        (s.breakthrough_quotes || []).filter(b => b.starred).map(b => ({ ...b, sessionDate: s.date }))
    );

    let y = drawHeader(
        doc, practiceName,
        'Your Coaching Journey',
        `${client.name} · Progress Report`,
        pageWidth, margin
    );

    // Date range and session count
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.slateLight);
    const dateRange = firstSession
        ? `${new Date(firstSession.date + 'T12:00:00').toLocaleDateString('default', { month: 'long', year: 'numeric' })} — ${new Date(lastSession.date + 'T12:00:00').toLocaleDateString('default', { month: 'long', year: 'numeric' })}`
        : '';
    doc.text(`${sessions.length} sessions completed  ·  ${dateRange}`, margin, y);
    y += 24;

    // Transformation arc
    if (options.arc && transformationArc?.arc) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 60);
        y = drawSectionTitle(doc, 'Your Journey', y, margin, pageWidth);
        if (transformationArc.arc.headline) {
            doc.setFont('helvetica', 'bolditalic');
            doc.setFontSize(13);
            doc.setTextColor(...COLORS.sage);
            const hLines = doc.splitTextToSize(transformationArc.arc.headline, pageWidth - margin * 2);
            hLines.forEach(line => {
                y = addPageIfNeeded(doc, y, margin, pageHeight, 18);
                doc.text(line, margin, y);
                y += 18;
            });
            y += 8;
        }
        if (transformationArc.arc.arc) {
            const paragraphs = transformationArc.arc.arc.split('\n\n');
            paragraphs.forEach(para => {
                y = drawBodyText(doc, para, y, margin, pageWidth, pageHeight);
                y += 4;
            });
        }
        y += 8;
    }

    // Achieved goals
    if (options.achievedGoals && achievedGoals.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Goals Achieved', y, margin, pageWidth);
        achievedGoals.forEach(g => { y = drawBullet(doc, g.text, y, margin, pageWidth, pageHeight); });
        y += 8;
    }

    // Active goals
    if (options.activeGoals && activeGoals.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Goals In Progress', y, margin, pageWidth);
        activeGoals.forEach(g => { y = drawBullet(doc, g.text, y, margin, pageWidth, pageHeight); });
        y += 8;
    }

    // Starred breakthrough moments
    if (options.breakthroughs && allStarred.length > 0) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 40);
        y = drawSectionTitle(doc, 'Breakthrough Moments', y, margin, pageWidth);
        allStarred.forEach(bq => {
            y = drawQuoteBlock(doc, bq.quote, y, margin, pageWidth, pageHeight);
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.slateLight);
            y = addPageIfNeeded(doc, y - 8, margin, pageHeight, 12);
            doc.text(new Date(bq.sessionDate + 'T12:00:00').toLocaleDateString(), margin + 10, y);
            y += 16;
        });
        y += 8;
    }

    // Custom coach message
    if (options.coachMessage && coachMessage?.trim()) {
        y = addPageIfNeeded(doc, y, margin, pageHeight, 60);
        y = drawSectionTitle(doc, 'A Note From Your Coach', y, margin, pageWidth);
        doc.setFillColor(235, 240, 237);
        const msgLines = doc.splitTextToSize(coachMessage, pageWidth - margin * 2 - 20);
        const msgHeight = msgLines.length * 14 + 20;
        doc.rect(margin, y - 6, pageWidth - margin * 2, msgHeight, 'F');
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.slate);
        msgLines.forEach(line => {
            y = addPageIfNeeded(doc, y, margin, pageHeight, 14);
            doc.text(line, margin + 10, y + 4);
            y += 14;
        });
        y += 20;
    }

    // Footers
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        drawFooter(doc, practiceName, i, totalPages, false, pageWidth, margin);
    }

    doc.save(`progress_report_${client.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
};